import { Link } from 'react-router-dom';
import { MessageCircle } from 'lucide-react';
import { usePageSeoContext } from '../../lib/PageSeoContext';
import PhoneReveal from '../ui/PhoneReveal';
import { WHATSAPP_BUTTON_TEXT_KEY, DEFAULT_WHATSAPP_BUTTON_TEXT, HOSTING_SINCE_YEAR_KEY, DEFAULT_HOSTING_SINCE_YEAR } from '../../lib/siteSettingsApi';

const linkClasses =
  'text-dark-text no-underline text-[14.5px] transition-colors duration-150 hover:text-paper active:text-dark-muted focus-visible:outline-none focus-visible:shadow-[0_0_0_2px_rgba(75,94,40,0.5)] rounded-sm';

const legalLinkClasses =
  'text-[13px] text-dark-muted no-underline transition-colors duration-150 hover:text-dark-text focus-visible:outline-none focus-visible:shadow-[0_0_0_2px_rgba(75,94,40,0.5)] rounded-sm';

const exploreLinks = [
  { to: '/rooms', label: 'Rooms & Suites' },
  { to: '/spaces', label: 'Spaces & Amenities' },
  { to: '/reviews', label: 'Guest Reviews' },
  { to: '/location', label: 'Location & Map' },
];

const aboutLinks = [
  { to: '/about', label: 'Our Story & Host' },
  { to: '/sustainability', label: 'Sustainability' },
  { to: '/faq', label: 'FAQ' },
  { to: '/contact', label: 'Contact' },
];

const socialLinks = [
  { key: 'social_instagram', label: 'Instagram' },
  { key: 'social_facebook', label: 'Facebook' },
  { key: 'social_youtube', label: 'YouTube' },
] as const;

export default function Footer() {
  const { siteSettings } = usePageSeoContext();
  const phone = siteSettings.contact_phone || '+91 75748 55500';
  const address = siteSettings.contact_address || 'Bhairavnath Road, Nr. Kankaria Lake, Ahmedabad, Gujarat';
  const whatsappDigits = siteSettings.social_whatsapp?.replace(/[^\d]/g, '') ?? '';
  const whatsappMessage = siteSettings[WHATSAPP_BUTTON_TEXT_KEY] || DEFAULT_WHATSAPP_BUTTON_TEXT;
  const hostingSinceYear = siteSettings[HOSTING_SINCE_YEAR_KEY] || DEFAULT_HOSTING_SINCE_YEAR;
  const activeSocials = socialLinks.filter((s) => siteSettings[s.key]);

  return (
    <footer className="bg-ink text-dark-text font-sans">
      <div className="max-w-page mx-auto px-8 pt-16 pb-8 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-[1.4fr_1fr_1fr_1fr] gap-10">
        <div>
          <div className="mb-4">
            <img src="/logo.webp" alt="Homeland Stay" width={330} height={112} className="h-11 w-auto" />
          </div>
          <p className="text-[14.5px] leading-relaxed text-dark-muted max-w-xs m-0 mb-4">
            A Gold Category private-floor homestay near Kankaria Lake, Ahmedabad — hosted by the Jani family since{' '}
            {hostingSinceYear}.
          </p>
          {(activeSocials.length > 0 || whatsappDigits) && (
            <div className="flex gap-3">
              {whatsappDigits && (
                <a
                  href={`https://wa.me/${whatsappDigits}?text=${encodeURIComponent(whatsappMessage)}`}
                  target="_blank"
                  rel="noopener noreferrer"
                  aria-label="WhatsApp"
                  className="w-9 h-9 rounded-full border border-dark-border flex items-center justify-center text-dark-muted hover:text-paper hover:border-olive transition-colors"
                >
                  <MessageCircle size={16} />
                </a>
              )}
              {activeSocials.map(({ key, label }) => (
                <a
                  key={key}
                  href={siteSettings[key]}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="h-9 px-3.5 rounded-full border border-dark-border flex items-center text-[12.5px] font-semibold text-dark-muted hover:text-paper hover:border-olive transition-colors"
                >
                  {label}
                </a>
              ))}
            </div>
          )}
        </div>

        <FooterColumn heading="Explore" links={exploreLinks} />
        <FooterColumn heading="About" links={aboutLinks} />

        <div>
          <div className="text-xs tracking-[0.1em] uppercase text-dark-muted mb-4">Visit</div>
          <div className="text-[14.5px] leading-loose text-dark-text">
            {address.split(',').map((line, i) => (
              <span key={i}>
                {line.trim()}
                <br />
              </span>
            ))}
            <br />
            <PhoneReveal phone={phone} variant="link" linkClassName={linkClasses} />
          </div>
        </div>
      </div>

      <div className="max-w-page mx-auto px-8 py-6 border-t border-dark-border flex justify-between flex-wrap gap-3">
        <span className="text-[13px] text-dark-muted">© 2026 Homeland Stay. All rights reserved.</span>
        <div className="flex gap-5 flex-wrap">
          <Link to="/privacy-policy" className={legalLinkClasses}>
            Privacy Policy
          </Link>
          <Link to="/terms-conditions" className={legalLinkClasses}>
            Terms &amp; Conditions
          </Link>
          <Link to="/cancellation-policy" className={legalLinkClasses}>
            Cancellation Policy
          </Link>
          <Link to="/sitemap" className={legalLinkClasses}>
            Sitemap
          </Link>
        </div>
      </div>
    </footer>
  );
}

function FooterColumn({ heading, links }: { heading: string; links: { to: string; label: string }[] }) {
  return (
    <div>
      <div className="text-xs tracking-[0.1em] uppercase text-dark-muted mb-4">{heading}</div>
      <div className="flex flex-col gap-2.5">
        {links.map((link) => (
          <Link key={link.to} to={link.to} className={linkClasses}>
            {link.label}
          </Link>
        ))}
      </div>
    </div>
  );
}
